// Chart data shapes shared by home (SCR-02), stats (SCR-05) and people (SCR-06/07) cards (plan 10.4, 10.5).
// The server returns numbers with display states; the web draws, labels (i18n by key) and writes the one-line summary.
import { z } from 'zod';
import { CostKind, DecisionActor, ToolDecision } from './enums.js';
import { Count, Delta, EpochMs, Pct, PersonRef, ValuedNumber } from './common.js';

/** Time buckets (plan 10.3.1 묶음 단위). auto: ≤1 day hour · 2–45 days day · 46–180 days week · 181+ days month. */
export const BUCKETS = ['hour', 'day', 'week', 'month'] as const;
export const Bucket = z.enum(BUCKETS);
export type Bucket = z.infer<typeof Bucket>;
export const autoBucket = (days: number): Bucket => (days <= 1 ? 'hour' : days <= 45 ? 'day' : days <= 180 ? 'week' : 'month');

/**
 * Reserved series/group keys: folded remainder ("기타") and merged small teams ("소규모 팀 합산", mockup key 'small').
 * Typical/slow latency series use 'p50'/'p95'. A diverging chart repeats a tool key once per `side`.
 */
export const OTHER_KEY = 'other';
export const SMALL_TEAMS_KEY = 'small_teams';
export const LATENCY_SERIES_KEYS = ['p50', 'p95'] as const;
/** Token kinds stacked by `by=token_kind` (4 non-overlapping kinds; reasoning is drawn dotted inside output). */
export const TOKEN_KIND_KEYS = ['new', 'cache_read', 'cache_write', 'output', 'reasoning'] as const;

/**
 * One series. `key` is the group key (a method, tool, model id, team id, user id, token kind…). `label` is sent only
 * for data-defined keys (model labels, team names, people, key aliases, folder labels, extensions); enumerated keys
 * are labelled by the web (i18n). `hatched` = API key calls (빗금), `dotted` = reasoning inside output (점 무늬).
 */
export const Series = z.object({
  key: z.string(),
  label: z.string().nullable(),
  hatched: z.boolean(),
  dotted: z.boolean().optional(),
  /** Not stacked/summed (e.g. reasoning tokens, Codex estimated lines). */
  excludedFromTotal: z.boolean().optional(),
  values: z.array(ValuedNumber),
  /** Distinct people per bucket — the denominator when agg = per_person (plan 10.3.1 "분모 표시"). */
  people: z.array(Count.nullable()).optional(),
  /**
   * Latency series: requests behind each bucket. The web blanks buckets under MIN_LATENCY_SAMPLES (5) and prints
   * "표본 적음" (mockup stats.js time-wait/api-calls); the value itself stays as computed.
   */
  samples: z.array(Count.nullable()).optional(),
  /** Diverging layout only (changes-lines): added lines point up, removed lines down. */
  side: z.enum(['up', 'down']).optional(),
});
export type Series = z.infer<typeof Series>;

/** A time-axis chart: stacked bar/area, bar, line, diverging (added up / removed down). */
export const TimeChart = z.object({
  shape: z.literal('time'),
  unit: Bucket,
  /** Bucket start instants (UTC ms); labels are formatted by the web in the display time zone. */
  buckets: z.array(EpochMs),
  series: z.array(Series),
  /** Previous equal-length period: ONE total per bucket, drawn as the grey dashed line (plan 10.5). */
  prev: z.array(z.number().nullable()).nullable(),
  total: ValuedNumber,
  prevTotal: ValuedNumber.nullable(),
  stacked: z.boolean(),
  /** Diverging layout (series carry `side`). */
  diverging: z.boolean().optional(),
  /** Index/value of the largest stacked bucket (summary "가장 많은 날"). */
  peak: z.object({ index: z.int().nonnegative(), value: z.number() }).nullable(),
});
export type TimeChart = z.infer<typeof TimeChart>;

/** One bar/slice. `parts` stacks a bar (e.g. opened vs edited, per tool). */
export const Group = z.object({
  key: z.string(),
  label: z.string().nullable(),
  hatched: z.boolean().optional(),
  value: ValuedNumber,
  /** Share of the shown total (0–100). */
  share: Pct.nullable().optional(),
  /** Denominator behind a ratio value (e.g. cache reuse "of 1.9만 read"). */
  denominator: z.number().nullable().optional(),
  people: Count.nullable().optional(),
  parts: z.array(z.object({ key: z.string(), value: z.number() })).optional(),
  /** Raw tool names behind an action group (tooltip). */
  rawNames: z.array(z.string()).optional(),
  /** Masked label (folder names without permission / small group): web shows "폴더 n". */
  masked: z.boolean().optional(),
  /** by = person: who the bar is (name per viewer permission) and whether the person page may be opened. */
  person: PersonRef.optional(),
  canOpen: z.boolean().optional(),
});
export type Group = z.infer<typeof Group>;

/** Ranked/compositional chart: horizontal bars, donut, plain table. `order` 'name' = 이름순(순위 아님). */
export const GroupChart = z.object({
  shape: z.literal('groups'),
  groups: z.array(Group),
  /** "기타 (k)" folded remainder, or null. */
  other: Group.extend({ count: Count }).nullable(),
  total: ValuedNumber,
  order: z.enum(['value', 'name']),
});
export type GroupChart = z.infer<typeof GroupChart>;

/** Weekday × hour grid, rows Mon..Sun (0..6), cols 0..23 in the display time zone. */
export const HeatChart = z.object({
  shape: z.literal('heatmap'),
  values: z.array(z.array(z.number().nonnegative()).length(24)).length(7),
  max: z.number().nonnegative(),
  total: z.number().nonnegative(),
});
export type HeatChart = z.infer<typeof HeatChart>;

/** Conversation-open-time bins (plan 10.2.5 time-open). */
export const OPEN_TIME_BINS = ['lt5m', 'm5to15', 'm15to30', 'm30to60', 'h1to2', 'ge2h'] as const;
export const OpenTimeBin = z.enum(OPEN_TIME_BINS);
export const HistogramChart = z.object({
  shape: z.literal('histogram'),
  bins: z.array(z.object({ bin: OpenTimeBin, counts: z.record(z.string(), Count) })),
  seriesKeys: z.array(z.string()),
  median: ValuedNumber,
});
export type HistogramChart = z.infer<typeof HistogramChart>;

/** actions-decisions: allow/reject/closed × who decided, as a 100% bar. */
export const DecisionsChart = z.object({
  shape: z.literal('decisions'),
  rows: z.array(z.object({ outcome: ToolDecision, byActor: z.partialRecord(DecisionActor, Count) })),
  total: Count,
});
export type DecisionsChart = z.infer<typeof DecisionsChart>;

/** api-reconcile: our price-table cost vs the org bill per UTC day × model × key (plan 10.2.8, 5.12). */
export const RECONCILE_STATUSES = ['ok', 'small', 'big', 'no_ours', 'no_org'] as const;
export const ReconcileStatus = z.enum(RECONCILE_STATUSES);
export const ReconcileChart = z.object({
  shape: z.literal('reconcile'),
  /** 'full' needs cost.view all; 'limited' shows only the viewer's keys' org totals. */
  mode: z.enum(['full', 'limited']),
  rows: z.array(
    z.object({
      date: z.string(),
      model: z.string().nullable(),
      apiKey: z.object({ id: z.string(), alias: z.string(), last4: z.string() }).nullable(),
      ours: ValuedNumber,
      org: ValuedNumber,
      diffMicros: z.int().nullable(),
      diffPct: z.number().nullable(),
      status: ReconcileStatus,
      tokens: Count.nullable(),
    }),
  ),
  counts: z.partialRecord(ReconcileStatus, Count),
  /** Key grouping not confirmed by the provider report → key column is "제공 안 됨". */
  keyGroupingAvailable: z.boolean(),
});
export type ReconcileChart = z.infer<typeof ReconcileChart>;

/** api-costs / SCR-15: cost kinds side by side, never summed (plan 6.4.4). */
export const CostsChart = z.object({
  shape: z.literal('costs'),
  /** `basis`: what each total rests on — conversations (CLI kinds), gateway calls, or org-report keys × days. */
  kinds: z.array(
    z.object({
      kind: CostKind,
      value: ValuedNumber,
      basis: z.object({ sessions: Count.nullable(), calls: Count.nullable(), keys: Count.nullable(), days: Count.nullable() }),
    }),
  ),
});
export type CostsChart = z.infer<typeof CostsChart>;

export const ChartData = z.discriminatedUnion('shape', [TimeChart, GroupChart, HeatChart, HistogramChart, DecisionsChart, ReconcileChart, CostsChart]);
export type ChartData = z.infer<typeof ChartData>;

/**
 * Facts for the one-sentence summary (plan 10.4.2). The web picks the sentence template by `kind`;
 * never names a person; 'no record' is reported in the footer, not here.
 */
export const SummaryFacts = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('total'), cur: z.number(), prev: z.number().nullable(), delta: Delta, perPerson: z.object({ value: z.number(), people: Count }).nullable() }),
  z.object({ kind: z.literal('top'), key: z.string(), label: z.string().nullable(), value: z.number(), share: Pct, total: z.number() }),
  z.object({ kind: z.literal('peak_cell'), weekday: z.int().min(0).max(6), hour: z.int().min(0).max(23), value: z.number() }),
  z.object({ kind: z.literal('top_bin'), bin: OpenTimeBin, count: Count, medianMs: z.number().nullable() }),
  z.object({ kind: z.literal('latency'), p50Ms: z.number().nullable(), p95Ms: z.number().nullable(), n: Count, tooFew: z.boolean() }),
  z.object({ kind: z.literal('costs'), byKind: z.partialRecord(CostKind, z.int()) }),
  z.object({ kind: z.literal('lines'), added: z.number(), removed: z.number(), codexEstimated: z.object({ added: z.number(), removed: z.number() }).nullable(), delta: Delta }),
  z.object({ kind: z.literal('decisions'), allowed: Count, topActor: DecisionActor.nullable(), share: Pct }),
  z.object({ kind: z.literal('reconcile'), n: Count, counts: z.partialRecord(ReconcileStatus, Count) }),
  z.object({ kind: z.literal('none') }),
]);
export type SummaryFacts = z.infer<typeof SummaryFacts>;

/** Two-way share that sums to 100 (CLI vs API). Smaller side < 10% keeps one decimal (mockup splitPct). */
export const SplitRatio = z.object({ cli: z.number().nonnegative(), api: z.number().nonnegative(), cliPct: Pct, apiPct: Pct });
export type SplitRatio = z.infer<typeof SplitRatio>;
export function splitRatio(cli: number, api: number): SplitRatio {
  const total = cli + api;
  if (!total) return { cli, api, cliPct: 0, apiPct: 0 };
  const raw = (cli / total) * 100;
  const small = Math.min(raw, 100 - raw);
  const cliPct = small < 10 ? Math.round(raw * 10) / 10 : Math.round(raw);
  return { cli, api, cliPct, apiPct: Math.round((100 - cliPct) * 10) / 10 };
}
